import type { Table } from '@tanstack/react-table';
import {
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type PropsWithChildren,
  useMemo,
} from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type {
  TableLevel,
  TableSize,
  TableSpacing,
  TableVariant,
} from '../../types';
import { TableContextProvider } from '../TableContextProvider';

export interface TableElementProps<RowData extends { id: string }>
  extends Omit<HTMLProps<HTMLTableElement>, 'className' | 'size' | 'style'>,
    PropsWithChildren {
  level?: TableLevel;
  size?: TableSize;
  spacing?: TableSpacing;
  stickyHeader?: boolean;
  table: Table<RowData>;
  variant?: TableVariant;
}

const tableVariants = variants({
  base: cn('w-full', 'table-fixed', 'border-separate', 'text-default'),
  variants: {
    spacing: {
      none: cn('border-spacing-0'),
      xs: cn('border-spacing-y-xs'),
      sm: cn('border-spacing-y-sm'),
      md: cn('border-spacing-y-md'),
      lg: cn('border-spacing-y-lg'),
      xl: cn('border-spacing-y-xl'),
    },
  },
});

function TableElementInner<RowData extends { id: string }>(
  {
    children,
    level = 2,
    size = 'md',
    spacing = 'none',
    stickyHeader = false,
    table,
    variant = 'default',
    ...props
  }: TableElementProps<RowData>,
  ref: ForwardedRef<HTMLTableElement>,
) {
  /**
   * Memoization of column size variables
   * This is done to ensure that the column sizes are recalculated only when necessary,
   *
   * https://tanstack.com/table/v8/docs/framework/react/examples/column-resizing-performant
   */

  const columns = table.getAllFlatColumns();
  const { columnSizingInfo, columnSizing } = table.getState();
  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};

    for (const header of headers) {
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }

    return colSizes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSizingInfo, columnSizing, columns]);

  return (
    <TableContextProvider
      level={level}
      size={size}
      spacing={spacing}
      stickyHeader={stickyHeader}
      variant={variant}
    >
      <table
        {...props}
        ref={ref}
        className={cn(tableVariants({ spacing }))}
        style={columnSizeVars}
      >
        {children}
      </table>
    </TableContextProvider>
  );
}

TableElementInner.displayName = 'TableElement';

export const TableElement = forwardRef(TableElementInner) as <
  RowData extends { id: string },
>(
  props: TableElementProps<RowData> & {
    ref?: ForwardedRef<HTMLTableElement>;
  },
) => ReturnType<typeof TableElementInner>;
